import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { apiClient } from "../lib/apiClient";
import { authService, User } from "../lib/authService";
import logger from "../utils/logger";
import errorReporting from "../utils/errorReporting";
import { languageCodeMap } from "../i18n";
import { useLanguage } from "./LanguageContext";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
  ) => Promise<{ user: User | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (token: string, newPassword: string) => Promise<void>;
  initiateOAuth: (
    provider: "google" | "github" | "microsoft",
    redirectUri: string,
  ) => Promise<{ authUrl?: string }>;
  handleOAuthCallback: (
    provider: "google" | "github" | "microsoft",
    code: string,
    redirectUri: string,
  ) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const { applyUserLanguage } = useLanguage();

  useEffect(() => {
    // Check for active sessions and set the user
    const checkAuthStatus = async () => {
      try {
        if (authService.isAuthenticated()) {
          // If we have a token, try to get the user
          const cachedUser = authService.getUser();
          if (cachedUser) {
            setUser(cachedUser);
          }

          // Verify with server and get fresh data
          const response = await authService.getCurrentUser();
          if (response.success && response.data.user) {
            setUser(response.data.user);

            // Set user in Sentry
            errorReporting.setUser({
              id: response.data.user.id,
              email: response.data.user.email,
            });
          } else {
            // Token might be invalid
            if (
              response.error === "Unauthorized" ||
              response.error === "Invalid token"
            ) {
              // Try refresh? authService should have handled it or we logout
              const refreshedToken = await authService.refreshTokens();
              if (!refreshedToken) {
                setUser(null);
                errorReporting.clearUser();
              }
            }
          }
        } else {
          setUser(null);
          errorReporting.clearUser();
        }
      } catch (error) {
        console.error("Failed to check auth status:", error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  useEffect(() => {
    if (user) {
      const fetchUserSettings = async () => {
        try {
          const settingsData = await apiClient.getUserSettings();

          if (settingsData?.settings?.nativeLanguage) {
            // Apply the user's native language to the app through the LanguageContext
            applyUserLanguage(settingsData.settings.nativeLanguage);
          } else {
            // If no user settings, use stored language preference or default
            const storedLang = localStorage.getItem("i18nextLng");
            if (storedLang) {
              // No need to call applyUserLanguage here as i18n is already initialized with the stored language
            }
          }
        } catch (error) {
          logger.error(
            "Error fetching user settings or language initialization:",
            error,
          );
        }
      };

      fetchUserSettings();

      // Also check if profile exists
      const checkUserProfile = async () => {
        try {
          await apiClient.getProfile();
        } catch (error) {
          logger.error("Error fetching user profile:", error);
        }
      };

      checkUserProfile();
    }
  }, [user, applyUserLanguage]);

  const signIn = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });

      if (!response.success || !response.data.user) {
        throw new Error(response.error || "Login failed");
      }

      setUser(response.data.user);

      // Add a breadcrumb for successful login
      errorReporting.addBreadcrumb({
        category: "auth",
        message: "User logged in successfully",
        level: "info",
      });

      errorReporting.setUser({
        id: response.data.user.id,
        email: response.data.user.email,
      });
    } catch (error) {
      logger.error("Sign in error:", error);
      errorReporting.captureException(error, { context: "signIn", email });
      throw error;
    }
  };

  const signUp = async (
    email: string,
    password: string,
    firstName?: string,
    lastName?: string,
  ) => {
    try {
      const response = await authService.register({
        email,
        password,
        firstName,
        lastName,
      });

      if (!response.success || !response.data.user) {
        throw new Error(response.error || "Registration failed");
      }

      setUser(response.data.user);

      // Add a breadcrumb for successful registration
      errorReporting.addBreadcrumb({
        category: "auth",
        message: "User registered successfully",
        level: "info",
      });

      errorReporting.setUser({
        id: response.data.user.id,
        email: response.data.user.email,
      });

      logger.info(`Successfully created new user ${response.data.user.id}`);

      return { user: response.data.user };
    } catch (error) {
      logger.error("Error in sign up:", error);
      errorReporting.captureException(error, { context: "signUp", email });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Add a breadcrumb before signing out
      errorReporting.addBreadcrumb({
        category: "auth",
        message: "User signed out",
        level: "info",
      });

      // Clear user from Sentry before actual sign out
      errorReporting.clearUser();

      await authService.logout();
    } catch (error) {
      logger.error("Sign out error:", error);
      errorReporting.captureException(error, { context: "signOut" });
      throw error;
    } finally {
      setUser(null);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await authService.forgotPassword(email);
      if (!response.success) {
        throw new Error(response.error || "Failed to send reset email");
      }
    } catch (error) {
      logger.error("Reset password error:", error);
      throw error;
    }
  };

  const confirmResetPassword = async (token: string, newPassword: string) => {
    try {
      const response = await authService.resetPassword(token, newPassword);
      if (!response.success) {
        throw new Error(response.error || "Failed to reset password");
      }
    } catch (error) {
      logger.error("Confirm reset password error:", error);
      throw error;
    }
  };

  const initiateOAuth = async (
    provider: "google" | "github" | "microsoft",
    redirectUri: string,
  ) => {
    try {
      const response = await authService.initiateOAuth(provider, redirectUri);
      if (!response.success) {
        throw new Error(response.error || "Failed to initiate OAuth");
      }
      return { authUrl: response.data.authUrl };
    } catch (error) {
      logger.error("OAuth initiation error:", error);
      throw error;
    }
  };

  const handleOAuthCallback = async (
    provider: "google" | "github" | "microsoft",
    code: string,
    redirectUri: string,
  ) => {
    try {
      const response = await authService.handleOAuthCallback(
        provider,
        code,
        redirectUri,
      );
      if (!response.success || !response.data.user) {
        throw new Error(response.error || "OAuth callback failed");
      }
      setUser(response.data.user);
      errorReporting.setUser({
        id: response.data.user.id,
        email: response.data.user.email,
      });
    } catch (error) {
      logger.error("OAuth callback error:", error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    confirmResetPassword,
    initiateOAuth,
    handleOAuthCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}